import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowRight } from "lucide-react";
import axios from "axios";
import { useCurrency } from "../context/CurrencyContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEBOUNCE_MS = 220;

const NavSearch = () => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();

  // Fetch suggestions (debounced). Empty q → top popular products.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/products/suggest`, {
          params: { q, limit: 6 },
        });
        if (cancelled) return;
        setResults(data?.results || []);
        setActiveIdx(-1);
      } catch {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q, open]);

  // Focus the input as soon as the panel opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  // Close on ESC + click outside
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => Math.min(i + 1, results.length - 1));
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => Math.max(i - 1, -1));
      }
      if (e.key === "Enter") {
        if (activeIdx >= 0 && results[activeIdx]) {
          goToProduct(results[activeIdx]);
        } else if (q.trim()) {
          goToSearchPage();
        }
      }
    };
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) close();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open, activeIdx, results, q]);

  const close = () => {
    setOpen(false);
    setActiveIdx(-1);
  };

  const goToProduct = (p) => {
    close();
    navigate(`/parfum/${p.slug}`);
  };

  const goToSearchPage = () => {
    close();
    navigate(`/recherche?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        data-testid="nav-search"
        onClick={() => setOpen((v) => !v)}
        className="hover:text-[color:var(--gold)] transition-colors"
        aria-label="Recherche"
      >
        <Search size={18} strokeWidth={1.5} />
      </button>

      {open && (
        <div
          data-testid="nav-search-panel"
          className="fixed left-0 right-0 top-[var(--nav-h,88px)] md:absolute md:left-auto md:right-0 md:top-full md:mt-4 z-[100]"
        >
          <div
            className="mx-auto md:mx-0 max-w-[600px] md:w-[420px] bg-[#0A0A0A] border border-[color:var(--gold)]/30 shadow-[0_25px_80px_rgba(0,0,0,0.6)]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Input row */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10">
              <Search size={16} className="text-[color:var(--gold)]" strokeWidth={1.5} />
              <input
                ref={inputRef}
                data-testid="nav-search-input"
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Chercher un parfum, une note, une marque…"
                className="flex-1 bg-transparent text-white placeholder-white/35 text-sm outline-none tracking-wide"
              />
              <button
                data-testid="nav-search-close"
                onClick={close}
                className="text-white/40 hover:text-[color:var(--gold)] transition-colors"
                aria-label="Fermer"
              >
                <X size={16} />
              </button>
            </div>

            {/* Results */}
            <div className="max-h-[60vh] overflow-y-auto">
              {loading && (
                <p className="px-5 py-6 text-white/40 text-xs label-caps">Recherche…</p>
              )}
              {!loading && results.length === 0 && (
                <p className="px-5 py-6 text-white/40 text-xs label-caps">Aucun résultat pour « {q} »</p>
              )}
              {!loading && results.length > 0 && (
                <>
                  {!q.trim() && (
                    <p className="px-5 pt-4 pb-2 text-[9px] tracking-[0.3em] uppercase text-white/40">
                      Suggestions
                    </p>
                  )}
                  <ul>
                    {results.map((p, i) => (
                      <li key={p.id}>
                        <button
                          data-testid={`nav-search-result-${i}`}
                          onClick={() => goToProduct(p)}
                          onMouseEnter={() => setActiveIdx(i)}
                          className={`w-full flex items-center gap-4 px-5 py-3 text-left transition-colors ${
                            activeIdx === i ? "bg-white/[0.04]" : "hover:bg-white/[0.03]"
                          }`}
                        >
                          {p.image_main ? (
                            <img
                              src={p.image_main}
                              alt=""
                              className="w-12 h-12 object-cover border border-white/10 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-12 h-12 border border-white/10 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-serif truncate">{p.name}</p>
                            <p className="text-white/40 text-[10px] tracking-[0.25em] uppercase truncate">
                              {p.brand || "MAISON DOUBA"}
                            </p>
                          </div>
                          <p className="text-[color:var(--gold)] text-sm font-serif whitespace-nowrap">
                            {formatPrice(p.price || p.price_100ml || 0)}
                          </p>
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>

            {/* Footer CTA */}
            {q.trim() && (
              <button
                data-testid="nav-search-see-all"
                onClick={goToSearchPage}
                className="w-full px-5 py-3 border-t border-white/10 text-[color:var(--gold)] label-caps text-[10px] flex items-center justify-center gap-2 hover:bg-white/[0.03] transition-colors"
              >
                Voir tous les résultats pour « {q} » <ArrowRight size={12} />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NavSearch;
